import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import ReportPdfColorModal from "../ReportPdfColorModal";

import axios from "axios";
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";

import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Divider,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Modal,
  Paper,
  Select,
  Stack,
  styled,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";

import {
  AssessmentOutlined,
  Close,
  DownloadOutlined,
  FilterAltOutlined,
  RestartAlt,
  Search,
} from "@mui/icons-material";

import { DEFAULT_PDF_COLORS, usePdfSettings } from "../../../layouts/PdfSettingsContext";
import { useBranch } from "../../../../components/layouts/BranchContext";


/* =========================================================
   API
========================================================= */

const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL ||
  "https://wims.revisetoexcel.com";

const API = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    Accept: "application/json",
    "Content-Type": "application/json",
  },
});

API.interceptors.request.use((config) => {
  const token =
    localStorage.getItem("accessToken") ||
    sessionStorage.getItem("accessToken") ||
    localStorage.getItem("access_token") ||
    sessionStorage.getItem("access_token") ||
    localStorage.getItem("token") ||
    sessionStorage.getItem("token");

  if (token) {
    config.headers = config.headers || {};
    config.headers.Authorization = token.startsWith("Bearer ")
      ? token
      : `Bearer ${token}`;
  }

  return config;
});

/* =========================================================
   COMMON HELPERS
========================================================= */

const pad2 = (value) => String(value).padStart(2, "0");

const formatDateInput = (date) => {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) {
    return "";
  }

  return `${date.getFullYear()}-${pad2(
    date.getMonth() + 1
  )}-${pad2(date.getDate())}`;
};

const getToday = () => formatDateInput(new Date());

const getOneMonthAgo = () => {
  const date = new Date();
  date.setMonth(date.getMonth() - 1);
  return formatDateInput(date);
};

const normalizeHex = (value, fallback = "#16355B") => {
  let hex = String(value || fallback)
    .replace("#", "")
    .trim();

  if (hex.length === 3) {
    hex = hex
      .split("")
      .map((char) => char + char)
      .join("");
  }

  if (
    hex.length !== 6 ||
    !/^[0-9a-fA-F]{6}$/.test(hex)
  ) {
    return fallback;
  }

  return `#${hex.toUpperCase()}`;
};

const hexToRgb = (hex) => {
  const clean = normalizeHex(hex)
    .replace("#", "");

  return [
    parseInt(clean.substring(0, 2), 16) || 0,
    parseInt(clean.substring(2, 4), 16) || 0,
    parseInt(clean.substring(4, 6), 16) || 0,
  ];
};

const getUser = () => {
  for (const key of ["user", "userData", "currentUser"]) {
    try {
      const raw =
        sessionStorage.getItem(key) ||
        localStorage.getItem(key);

      if (raw) {
        return JSON.parse(raw) || {};
      }
    } catch (error) {
      console.error("User parse error:", error);
    }
  }

  return {};
};

const getAbsoluteImageUrl = (value) => {
  if (!value) return "";

  if (/^https?:\/\//i.test(value)) {
    return value;
  }

  if (String(value).startsWith("data:image/")) {
    return value;
  }

  const base = String(API_BASE_URL).replace(/\/$/, "");

  return `${base}${String(value).startsWith("/") ? "" : "/"}${value}`;
};

const blobToDataUrl = async (blob) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });

const convertDataUrlToPng = async (dataUrl) =>
  new Promise((resolve, reject) => {
    const image = new Image();

    image.onload = () => {
      const canvas = document.createElement("canvas");
      canvas.width = image.naturalWidth || image.width;
      canvas.height = image.naturalHeight || image.height;

      const ctx = canvas.getContext("2d");
      if (!ctx) {
        reject(new Error("Unable to create canvas context."));
        return;
      }

      ctx.drawImage(image, 0, 0);
      resolve(canvas.toDataURL("image/png"));
    };

    image.onerror = reject;
    image.src = dataUrl;
  });

const imageUrlToDataUrl = async (url) => {
  if (!url) return "";

  if (String(url).startsWith("data:image/")) {
    return convertDataUrlToPng(url);
  }

  const response = await fetch(url);
  if (!response.ok) {
    throw new Error("Unable to load branch image.");
  }

  const blob = await response.blob();
  const dataUrl = await blobToDataUrl(blob);
  return convertDataUrlToPng(dataUrl);
};

const formatDate = (value) => {
  if (!value) return "—";

  if (
    typeof value === "string" &&
    /^\d{4}-\d{2}-\d{2}$/.test(value)
  ) {
    const [year, month, day] = value.split("-");
    return `${day}/${month}/${year}`;
  }

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return String(value);
  }

  return date.toLocaleDateString("en-GB");
};

const formatStatus = (value) => {
  if (
    value === null ||
    value === undefined ||
    value === ""
  ) {
    return "—";
  }

  return (
    String(value).charAt(0).toUpperCase() +
    String(value).slice(1)
  );
};

const safeText = (value, fallback = "—") => {
  if (
    value === null ||
    value === undefined ||
    value === ""
  ) {
    return fallback;
  }

  if (
    typeof value === "string" ||
    typeof value === "number" ||
    typeof value === "boolean"
  ) {
    return String(value);
  }

  if (Array.isArray(value)) {
    return (
      value
        .map((item) => safeText(item, ""))
        .filter(Boolean)
        .join(", ") || fallback
    );
  }

  if (typeof value === "object") {
    return safeText(
      value?.name ??
        value?.label ??
        value?.title ??
        value?.display_name ??
        value?.id,
      fallback
    );
  }

  return fallback;
};

const getErrorMessage = (error) => {
  const status = error?.response?.status;
  const data = error?.response?.data;

  if (status === 401) {
    return "Authentication failed. Please login again.";
  }

  if (status === 403) {
    return "You do not have permission to access this report.";
  }

  if (status === 404) {
    return "Report API endpoint was not found.";
  }

  if (typeof data === "string") {
    return data;
  }

  if (data?.detail) {
    return data.detail;
  }

  if (data?.message) {
    return data.message;
  }

  if (data?.error) {
    return safeText(data.error);
  }

  return error?.message || "Something went wrong.";
};

/* =========================================================
   BRANCH / EMPLOYEE / STUDENT HELPERS
========================================================= */

const getBranchId = (branch) =>
  branch?.id ??
  branch?.branch_id ??
  branch?.value ??
  "";

const getBranchName = (branch) =>
  branch?.name ||
  branch?.branch_name ||
  `Branch ${getBranchId(branch)}`;

const getEmployeeId = (employee) =>
  employee?.employee ??
  employee?.employee_id ??
  employee?.id ??
  "";

const getEmployeeLabel = (employee) => {
  const name =
    employee?.employee_name ||
    "Unknown Employee";

  const code =
    employee?.employee_code || "";

  return code
    ? `${name} (${code})`
    : name;
};

const getStudentId = (student) =>
  student?.admission ??
  student?.student_id ??
  student?.student ??
  student?.id ??
  "";

const getStudentLabel = (student) => {
  const name =
    student?.student_name ||
    student?.candidate_name ||
    student?.studentName ||
    student?.name ||
    "Unknown Student";

  const code =
    student?.admission_code ||
    student?.student_code ||
    "";

  return code
    ? `${name} (${code})`
    : name;
};

/* =========================================================
   PROFESSIONAL REPORT TABLE
========================================================= */

const CustomTableScrollbar = styled("div")(({ theme }) => ({
  width: "100%",
  overflowX: "auto",
  overflowY: "auto",

  "&::-webkit-scrollbar": {
    width: 7,
    height: 8,
  },

  "&::-webkit-scrollbar-thumb": {
    backgroundColor:
      theme.palette.mode === "dark"
        ? "rgba(255,255,255,0.24)"
        : "rgba(15,23,42,0.22)",
    borderRadius: 10,
  },

  "&::-webkit-scrollbar-track": {
    backgroundColor:
      theme.palette.mode === "dark"
        ? "rgba(255,255,255,0.04)"
        : "rgba(15,23,42,0.04)",
  },

  scrollbarWidth: "thin",
}));

const ReportTable = ({
  title,
  rows = [],
  columns = [],
  loading = false,
  search = "",
  setSearch,
}) => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  useEffect(() => {
    setPage(0);
  }, [search, rows.length]);

  const filteredRows = useMemo(() => {
    const keyword = search.trim().toLowerCase();

    if (!keyword) return rows;

    return rows.filter((row) =>
      columns.some((column) => {
        const value = row?.[column];
        return safeText(value, "")
          .toLowerCase()
          .includes(keyword);
      })
    );
  }, [rows, columns, search]);

  const visibleRows = useMemo(
    () =>
      filteredRows.slice(
        page * rowsPerPage,
        page * rowsPerPage + rowsPerPage
      ),
    [filteredRows, page, rowsPerPage]
  );

  return (
    <Box
      sx={{
        mt: 2,
        border: 1,
        borderColor: "divider",
        borderRadius: 2.5,
        overflow: "hidden",
        backgroundColor: "transparent",
        boxShadow: "none",
      }}
    >
     

      {/* TABLE */}
      <TableContainer
        component={Paper}
        elevation={0}
        sx={{
          borderRadius: 0,
          backgroundColor: "background.paper",
          boxShadow: "none",
        }}
      >
        <CustomTableScrollbar>
          <Table
            stickyHeader
            size="small"
            sx={{
              minWidth: 1120,
              "& .MuiTableCell-root": {
                borderColor: "divider",
              },
              "& .MuiTableBody-root .MuiTableRow-root": {
                backgroundColor: "background.paper",
              },
              "& .MuiTableBody-root .MuiTableRow-root:nth-of-type(even)": {
                backgroundColor: "background.paper",
              },
              "& .MuiTableBody-root .MuiTableRow-root:hover": {
                backgroundColor: "action.hover",
              },
            }}
          >
            <TableHead>
              <TableRow>
                <TableCell
                  sx={{
                    position: "sticky",
                    left: 0,
                    zIndex: 4,
                    width: 58,
                    minWidth: 58,
                    fontWeight: 700,
                    fontSize: ".78rem",
                    whiteSpace: "nowrap",
                    backgroundColor: "primary.main",
                    color: "primary.contrastText",
                    py: 1.7,
                    px: 1.5,
                  }}
                >
                  #
                </TableCell>

                {columns.map((column) => (
                  <TableCell
                    key={column}
                    sx={{
                      minWidth: 145,
                      fontWeight: 700,
                      fontSize: ".78rem",
                      whiteSpace: "nowrap",
                      backgroundColor: "primary.main",
                      color: "primary.contrastText",
                      py: 1.7,
                      px: 2,
                      "&.MuiTableCell-stickyHeader": {
                        backgroundColor: "primary.main",
                        color: "primary.contrastText",
                      },
                    }}
                  >
                    {column
                      .replace(/_/g, " ")
                      .replace(/\b\w/g, (char) =>
                        char.toUpperCase()
                      )}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>

            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell
                    colSpan={columns.length + 1}
                    align="center"
                    sx={{ py: 8 }}
                  >
                    <CircularProgress size={30} />

                    <Typography
                      variant="body2"
                      sx={{
                        mt: 1.2,
                        color: "text.disabled",
                      }}
                    >
                      Loading report...
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : visibleRows.length > 0 ? (
                visibleRows.map((row, index) => (
                  <TableRow
                    hover
                    key={
                      row?.id ??
                      `${page}-${index}`
                    }
                    sx={{
                      backgroundColor: "background.paper",

                      "&:nth-of-type(even)": {
                        backgroundColor: "background.paper",
                      },

                      "&:hover": {
                        backgroundColor: "action.hover",
                      },

                      "& .MuiTableCell-root": {
                        backgroundColor: "background.paper",
                      },

                      "&:hover .MuiTableCell-root": {
                        backgroundColor: "action.hover",
                      },
                    }}
                  >
                    <TableCell
                      sx={{
                        position: "sticky",
                        left: 0,
                        zIndex: 2,
                        backgroundColor: "background.paper",
                        fontWeight: 600,
                        py: 1.55,
                        px: 1.5,
                        fontSize: ".83rem",
                        "tr:hover &": {
                          backgroundColor: "action.hover",
                        },
                      }}
                    >
                      {page * rowsPerPage + index + 1}
                    </TableCell>

                    {columns.map((column) => (
                      <TableCell
                        key={column}
                        sx={{
                          py: 1.55,
                          px: 2,
                          fontSize: ".83rem",
                          whiteSpace: "nowrap",
                          color: "text.primary",
                          backgroundColor: "background.paper",
                          "tr:hover &": {
                            backgroundColor: "action.hover",
                          },
                        }}
                      >
                        {column === "date"
                          ? formatDate(row?.[column])
                          : column === "status"
                          ? formatStatus(row?.[column])
                          : column === "attendance_percentage"
                          ? `${safeText(row?.[column], "0")} %`
                          : safeText(row?.[column])}
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell
                    colSpan={columns.length + 1}
                    align="center"
                    sx={{ py: 8 }}
                  >
                    <AssessmentOutlined
                      sx={{
                        fontSize: 40,
                        color: "text.disabled",
                        mb: 1,
                      }}
                    />

                    <Typography fontWeight={600}>
                      No records found
                    </Typography>

                    <Typography
                      variant="body2"
                      sx={{
                        mt: 0.5,
                        color: "text.disabled",
                      }}
                    >
                      Try changing the filters or
                      generate the report.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CustomTableScrollbar>

        <TablePagination
          component="div"
          count={filteredRows.length}
          page={page}
          onPageChange={(_, newPage) =>
            setPage(newPage)
          }
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(event) => {
            setRowsPerPage(
              Number(event.target.value)
            );
            setPage(0);
          }}
          rowsPerPageOptions={[
            10,
            25,
            50,
            100,
          ]}
          sx={{
           
            backgroundColor: "unset",
            ".MuiTablePagination-toolbar": {
              minHeight: 54,
              px: {
                xs: 1,
                md: 2,
              },
            },
          }}
        />
      </TableContainer>
    </Box>
  );
};

/* =========================================================
   FILTER MODAL
========================================================= */
const FilterModal = ({
  open,
  onClose,
  title,
  selectedId,
  setSelectedId,
  options,
  loadingOptions,
  getOptionId,
  getOptionLabel,
  optionLabel,
  dateFrom,
  dateTo,
  setDateFrom,
  setDateTo,
  onReset,
  onApply,
}) => {
  const theme = useTheme();

  return (
    <Modal
      open={open}
      onClose={onClose}
      BackdropProps={{
        sx: {
          backgroundColor: "rgba(15,23,42,0.58)",
          backdropFilter: "blur(3px)",
        },
      }}
    >
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: {
            xs: "94%",
            sm: 620,
            md: 720,
          },
          maxHeight: "90vh",
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
          bgcolor: "background.paper",
          color: "text.primary",
          borderRadius: 3,
          outline: "none",
          boxShadow:
            theme.palette.mode === "dark"
              ? "0 20px 60px rgba(0,0,0,0.55)"
              : "0 20px 60px rgba(0,0,0,0.18)",
          border:
            theme.palette.mode === "dark"
              ? `1px solid ${theme.palette.divider}`
              : "none",
        }}
      >
        {/* HEADER */}
        <Box
          sx={{
            px: { xs: 2, sm: 3 },
            py: 2.1,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 2,
            borderBottom: 1,
            borderColor: "divider",
            position: "sticky",
            top: 0,
            zIndex: 3,
            backgroundColor: "background.paper",
            flexShrink: 0,
          }}
        >
          <Box sx={{ minWidth: 0 }}>
            <Typography
              sx={{
                fontSize: 20,
                fontWeight: 700,
              }}
            >
              {title} Filters
            </Typography>

            <Typography
              variant="caption"
              sx={{
                color: "text.disabled",
              }}
            >
              Configure filters and apply the report.
            </Typography>
          </Box>

          <IconButton
            onClick={onClose}
            size="small"
            sx={{
              border: 1,
              borderColor: "divider",
              borderRadius: 2,
              flexShrink: 0,
            }}
          >
            <Close fontSize="small" />
          </IconButton>
        </Box>

        {/* CONTENT */}
        <Box
          sx={{
            p: { xs: 2, sm: 3 },
            maxHeight: "60vh",
            overflowY: "auto",
            minHeight: 0,
          }}
        >
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "1fr",
                sm: "repeat(2, minmax(0, 1fr))",
              },
              gap: 2,
            }}
          >
            {/* OPTION */}
            <Box
              sx={{
                gridColumn: {
                  xs: "span 1",
                  sm: "span 2",
                },
                p: 1.75,
                
                borderRadius: 2.5,
                backgroundColor: "background.paper",
              }}
            >
            

              <FormControl fullWidth size="small">
                <InputLabel>{optionLabel}</InputLabel>

                <Select
                  value={selectedId}
                  label={optionLabel}
                  onChange={(event) =>
                    setSelectedId(String(event.target.value))
                  }
                >
                  <MenuItem value="">
                    All {optionLabel}s
                  </MenuItem>

                  {loadingOptions ? (
                    <MenuItem disabled>
                      Loading...
                    </MenuItem>
                  ) : options.length > 0 ? (
                    options.map((item) => (
                      <MenuItem
                        key={getOptionId(item)}
                        value={String(getOptionId(item))}
                      >
                        {getOptionLabel(item)}
                      </MenuItem>
                    ))
                  ) : (
                    <MenuItem disabled>
                      No {optionLabel.toLowerCase()} found
                    </MenuItem>
                  )}
                </Select>
              </FormControl>
            </Box>

            {/* FROM DATE */}
            <Box
              sx={{
                p: 1.75,
              
                backgroundColor: "background.paper",
              }}
            >
             

              <TextField
                fullWidth
                size="small"
                type="date"
                label="From Date"
                value={dateFrom}
                onChange={(event) =>
                  setDateFrom(event.target.value)
                }
                InputLabelProps={{
                  shrink: true,
                }}
                inputProps={{
                  max: dateTo || undefined,
                }}
              />
            </Box>

            {/* TO DATE */}
            <Box
              sx={{
                p: 1.75,
                
                borderRadius: 2.5,
                backgroundColor: "background.paper",
              }}
            >
          
              <TextField
                fullWidth
                size="small"
                type="date"
                label="To Date"
                value={dateTo}
                onChange={(event) =>
                  setDateTo(event.target.value)
                }
                InputLabelProps={{
                  shrink: true,
                }}
                inputProps={{
                  min: dateFrom || undefined,
                }}
              />
            </Box>
          </Box>

          {/* FOOTER */}
          <Divider
            sx={{
              mt: 2.5,
              mb: 2,
            }}
          />

          <Box
            sx={{
              display: "flex",
              flexDirection: {
                xs: "column",
                sm: "row",
              },
              alignItems: {
                xs: "stretch",
                sm: "center",
              },
              justifyContent: "space-between",
              gap: 1.5,
              position: "sticky",
              bottom: 0,
              zIndex: 3,
              py: 1.5,
              backgroundColor: "background.paper",
              borderTop: "1px solid",
              borderColor: "divider",
            }}
          >
            <Button
              variant="text"
              startIcon={<RestartAlt />}
              onClick={onReset}
              sx={{
                textTransform: "none",
                color: "text.primary",
                fontWeight: 600,
                alignSelf: {
                  xs: "flex-start",
                  sm: "auto",
                },
              }}
            >
              Reset
            </Button>

            <Box
              sx={{
                display: "flex",
                flexDirection: {
                  xs: "column",
                  sm: "row",
                },
                gap: 1,
                width: {
                  xs: "100%",
                  sm: "auto",
                },
              }}
            >
              <Button
                variant="contained"
                onClick={onClose}
                sx={{
                  textTransform: "none",
                  borderRadius: 2,
                  width: {
                    xs: "100%",
                    sm: "auto",
                  },
                }}
               data-app-cancel="true">
                Cancel
              </Button>

              <Button
                variant="contained"
                startIcon={<FilterAltOutlined />}
                onClick={onApply}
                disabled={Boolean(
                  dateFrom &&
                    dateTo &&
                    dateFrom > dateTo
                )}
                sx={{
                  textTransform: "none",
                  borderRadius: 2,
                  px: 2.5,
                  fontWeight: 600,
                  width: {
                    xs: "100%",
                    sm: "auto",
                  },
                }}
              >
                Apply Filters
              </Button>
            </Box>
          </Box>
        </Box>
      </Box>
    </Modal>
  );
};

/* =========================================================
   PDF COLOR MODAL
========================================================= */

const PdfColorModal = ({
  open,
  onClose,
  themeColor,
  textColor,
  setThemeColor,
  setTextColor,
  onGenerate,
  loading,
  reportTitle,
}) => (
  <ReportPdfColorModal
    open={open}
    onClose={onClose}
    themeColor={themeColor}
    setThemeColor={setThemeColor}
    textColor={textColor}
    setTextColor={setTextColor}
    reportTitle={reportTitle || "Report Title"}
    onGenerate={onGenerate}
    loading={loading}
    disabled={loading}
  />
);

/* =========================================================
   REPORT HEADER
========================================================= */

const ReportHeader = ({
  title,
  subtitle,
  onFilter,
  onGenerate,
  onPdf,
  onExcel,
  loading,
  disableExport,
  search,
  setSearch,
}) => {
  return (
    <Box
      sx={{
        px: { xs: 1.5, md: 2.5 },
        py: { xs: 1.5, md: 2 },
        display: "flex",
        flexDirection: {
          xs: "column",
          lg: "row",
        },
        alignItems: {
          xs: "stretch",
          lg: "center",
        },
        justifyContent: "space-between",
        gap: 2,
      }}
    >
      <Stack
        direction={{ xs: "column", md: "row" }}
        spacing={{ xs: 1.2, md: 2 }}
        alignItems={{ xs: "stretch", md: "center" }}
        sx={{ minWidth: 0, flex: 1 }}
      >
        <TextField
          size="small"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          placeholder={`Search ${title.toLowerCase()}...`}
          sx={{
            width: { xs: "100%", sm: 300, md: 330 },
            flexShrink: 0,
            "& .MuiOutlinedInput-root": {
              borderRadius: 2,
              backgroundColor: "transparent",
            },
          }}
          InputProps={{
            startAdornment: (
              <Search
                sx={{
                  mr: 1,
                  fontSize: 20,
                  color: "text.disabled",
                }}
              />
            ),
          }}
        />

      </Stack>

      <Stack
        direction={{
          xs: "column",
          sm: "row",
        }}
        spacing={1}
        useFlexGap
        flexWrap="wrap"
        sx={{
          width: {
            xs: "100%",
            lg: "auto",
          },
        }}
      >
        <Button
          variant="contained"
          startIcon={<FilterAltOutlined />}
          onClick={onFilter}
          disabled={loading}
          sx={{
            textTransform: "none",
            borderRadius: 2,
            fontWeight: 600,
          }}
        >
          Filter
        </Button>

        <Button
          variant="contained"
          startIcon={<AssessmentOutlined />}
          onClick={onGenerate}
          disabled={loading}
          sx={{
            textTransform: "none",
            borderRadius: 2,
            fontWeight: 600,
          }}
        >
          Generate Report
        </Button>

        <Button
          variant="contained"
          color="success"
          startIcon={<DownloadOutlined />}
          onClick={onExcel}
          disabled={loading || disableExport}
          sx={{
            textTransform: "none",
            borderRadius: 2,
            fontWeight: 600,
          }}
        >
          Export Excel
        </Button>
      </Stack>
    </Box>
  );
};

/* =========================================================
   MAIN
========================================================= */

const AttendanceReport = () => {
  const theme = useTheme();

  const user = useMemo(
    () => getUser(),
    []
  );

  const userOrganizationId =
    user?.organization_id ||
    user?.organization?.id ||
    "";

  const {
    selectedBranchId,
    branchList,
    organizationId: contextOrganizationId,
  } = useBranch();

  const globalBranchId =
    selectedBranchId || user?.branch_id || "";

  const organizationId =
    contextOrganizationId ||
    userOrganizationId ||
    "";

  const organizationName =
    user?.organization_name ||
    user?.organizationName ||
    user?.organization?.name ||
    "Organization";

  const {
    pdfThemeColor,
    pdfTextColor,
    updatePdfThemeColor,
    updatePdfTextColor,
  } = usePdfSettings();

  const [tempPdfThemeColor, setTempPdfThemeColor] =
    useState(
      normalizeHex(
        pdfThemeColor,
        DEFAULT_PDF_COLORS.primary
      )
    );

  const [tempPdfTextColor, setTempPdfTextColor] =
    useState(
      normalizeHex(
        pdfTextColor,
        DEFAULT_PDF_COLORS.bodyText
      )
    );

  /* STAFF FILTERS */
  const [staffBranch, setStaffBranch] =
    useState(globalBranchId ? String(globalBranchId) : "");

  const [staffEmployeeId, setStaffEmployeeId] =
    useState("");

  const [staffDateFrom, setStaffDateFrom] =
    useState(getOneMonthAgo());

  const [staffDateTo, setStaffDateTo] =
    useState(getToday());

  /* STUDENT FILTERS */
  const [studentBranch, setStudentBranch] =
    useState(globalBranchId ? String(globalBranchId) : "");

  const [studentId, setStudentId] =
    useState("");

  const [studentDateFrom, setStudentDateFrom] =
    useState(getOneMonthAgo());

  const [studentDateTo, setStudentDateTo] =
    useState(getToday());

  /* OPTIONS */
  const [employeeOptions, setEmployeeOptions] =
    useState([]);

  const [studentOptions, setStudentOptions] =
    useState([]);

  const [employeeLoading, setEmployeeLoading] =
    useState(false);

  const [studentLoading, setStudentLoading] =
    useState(false);

  /* DATA */
  const [staffRows, setStaffRows] =
    useState([]);

  const [studentRows, setStudentRows] =
    useState([]);

  /* SEARCH */
  const [staffSearch, setStaffSearch] =
    useState("");

  const [studentSearch, setStudentSearch] =
    useState("");

  /* REPORT LOADING */
  const [staffReportLoading, setStaffReportLoading] =
    useState(false);

  const [studentReportLoading, setStudentReportLoading] =
    useState(false);

  const [pdfLoading, setPdfLoading] =
    useState(false);

  const [downloadLoading, setDownloadLoading] =
    useState(false);

  /* TABS */
  const [reportTab, setReportTab] =
    useState("staff");

  /* MODALS */
  const [staffFilterOpen, setStaffFilterOpen] =
    useState(false);

  const [studentFilterOpen, setStudentFilterOpen] =
    useState(false);

  const [pdfReportType, setPdfReportType] =
    useState(null);

  const [error, setError] = useState("");

  /* =========================================================
     GLOBAL BRANCH
  ========================================================= */

  const [branchDetails, setBranchDetails] = useState(null);
  const [branchLogo, setBranchLogo] = useState("");
  const [branchName, setBranchName] = useState("");

  const globalBranchName = useMemo(() => {
    const contextBranch = (branchList || []).find(
      (branch) =>
        String(getBranchId(branch)) ===
        String(globalBranchId)
    );

    return (
      branchName ||
      branchDetails?.name ||
      branchDetails?.branch_name ||
      (contextBranch ? getBranchName(contextBranch) : "")
    );
  }, [branchList, globalBranchId, branchName]);

  const fetchBranchDetails = useCallback(
    async (branchId) => {
      if (!branchId) {
        setBranchDetails(null);
        setBranchLogo("");
        setBranchName("");
        return;
      }

      try {
        const response = await API.get(
          `/api/master/branch/${branchId}/`
        );

        const data = response.data || {};

        const name =
          data?.name ||
          data?.branch_name ||
          data?.branchName ||
          "";

        const image =
          data?.image ||
          data?.logo ||
          data?.branch_logo ||
          data?.branchLogo ||
          "";

        setBranchDetails(data);
        setBranchName(name);

        if (image) {
          try {
            const absoluteUrl = getAbsoluteImageUrl(image);
            const logoDataUrl = await imageUrlToDataUrl(absoluteUrl);
            setBranchLogo(logoDataUrl);
          } catch (logoError) {
            console.error(
              "Failed to convert branch logo:",
              logoError
            );
            setBranchLogo("");
          }
        } else {
          setBranchLogo("");
        }
      } catch (fetchError) {
        console.error(
          "Branch details API error:",
          fetchError
        );

        setBranchDetails(null);
        setBranchLogo("");
      }
    },
    []
  );

  /* =========================================================
     NOTIFICATION
     Uses the existing error area for PDF feedback.
  ========================================================= */

  const notify = useCallback((message) => {
    setError(message || "");
  }, []);

  useEffect(() => {
    const branchId = globalBranchId
      ? String(globalBranchId)
      : "";

    setStaffBranch(branchId);
    setStudentBranch(branchId);
    setStaffEmployeeId("");
    setStudentId("");
    fetchBranchDetails(branchId);
  }, [globalBranchId, fetchBranchDetails]);

  /* =========================================================
     STAFF OPTIONS
  ========================================================= */

  const fetchEmployeeOptions = useCallback(
    async () => {
      if (
        !organizationId ||
        !staffBranch
      ) {
        setEmployeeOptions([]);
        return;
      }

      setEmployeeLoading(true);

      try {
        const response = await API.get(
          "/api/staff/attendance/",
          {
            params: {
              organization: organizationId,
              branch: staffBranch,
            },
          }
        );

        const records = Array.isArray(
          response.data?.records
        )
          ? response.data.records
          : [];

        const uniqueEmployees =
          records.filter(
            (item, index, array) =>
              index ===
              array.findIndex(
                (employee) =>
                  String(
                    getEmployeeId(
                      employee
                    )
                  ) ===
                  String(
                    getEmployeeId(item)
                  )
              )
          );

        setEmployeeOptions(
          uniqueEmployees
        );
      } catch (fetchError) {
        console.error(
          "Employee API error:",
          fetchError
        );

        setEmployeeOptions([]);
      } finally {
        setEmployeeLoading(false);
      }
    },
    [organizationId, staffBranch]
  );

  useEffect(() => {
    fetchEmployeeOptions();
  }, [fetchEmployeeOptions]);

  /* =========================================================
     STUDENT OPTIONS
  ========================================================= */

  const fetchStudentOptions = useCallback(
    async () => {
      if (
        !organizationId ||
        !studentBranch
      ) {
        setStudentOptions([]);
        return;
      }

      setStudentLoading(true);

      try {
        const response = await API.get(
          "/api/admission/attendance/",
          {
            params: {
              organization: organizationId,
              branch: studentBranch,
            },
          }
        );

        const records = Array.isArray(
          response.data?.records
        )
          ? response.data.records
          : Array.isArray(response.data)
          ? response.data
          : [];

        const uniqueStudents =
          records.filter(
            (item, index, array) =>
              index ===
              array.findIndex(
                (student) =>
                  String(
                    getStudentId(
                      student
                    )
                  ) ===
                  String(
                    getStudentId(item)
                  )
              )
          );

        setStudentOptions(
          uniqueStudents
        );
      } catch (fetchError) {
        console.error(
          "Student API error:",
          fetchError
        );

        setStudentOptions([]);
      } finally {
        setStudentLoading(false);
      }
    },
    [organizationId, studentBranch]
  );

  useEffect(() => {
    fetchStudentOptions();
  }, [fetchStudentOptions]);

  /* =========================================================
     STAFF REPORT
  ========================================================= */

  const generateStaffReport = useCallback(
    async () => {
      if (!organizationId) {
        setError(
          "Organization ID not found."
        );
        return [];
      }

      if (staffDateFrom > staffDateTo) {
        setError(
          "From Date cannot be greater than To Date."
        );
        return [];
      }

      setStaffReportLoading(true);
      setError("");

      try {
        const payload = {
          fromDate: staffDateFrom,
          toDate: staffDateTo,
          organizationId,

          ...(globalBranchId
            ? {
                branch: String(globalBranchId),
              }
            : {}),

          ...(staffEmployeeId
            ? {
                employeeId:
                  staffEmployeeId,
              }
            : {}),
        };

        console.log("STAFF ATTENDANCE REPORT REQUEST:", payload);

        const response = await API.post(
          "/api/reports/attendance-report/",
          payload
        );

        console.log("STAFF ATTENDANCE REPORT RESPONSE:", response.data);

        const records = Array.isArray(
          response.data?.results
        )
          ? response.data.results
          : Array.isArray(response.data?.records)
          ? response.data.records
          : [];

        const mappedRecords =
          records.map((item, index) => ({
            id:
              item?.id ??
              `staff-${index}`,
            employee_name:
              item?.employee_name ||
              "Unknown Employee",
            employee_code:
              item?.employee_code || "—",
            date:
              item?.date || "",
            day:
              item?.date
                ? new Date(`${item.date}T00:00:00`).toLocaleDateString(
                    "en-US",
                    { weekday: "short" }
                  )
                : "—",
            status:
              item?.status || "—",
            time_in:
              item?.time_in || "—",
            time_out:
              item?.time_out || "—",
            total_hours:
              item?.total_hours ?? "—",
            remark:
              item?.remark || "—",
          }));

        setStaffRows(mappedRecords);
        return mappedRecords;
      } catch (fetchError) {
        console.error(
          "Staff report error:",
          fetchError
        );

        setStaffRows([]);
        setError(
          getErrorMessage(fetchError)
        );

        return [];
      } finally {
        setStaffReportLoading(false);
      }
    },
    [
      organizationId,
      staffDateFrom,
      staffDateTo,
      globalBranchId,
      staffEmployeeId,
    ]
  );

  /* =========================================================
     STUDENT REPORT
  ========================================================= */

  const generateStudentReport = useCallback(
    async () => {
      if (!organizationId) {
        setError(
          "Organization ID not found."
        );
        return [];
      }

      if (
        studentDateFrom >
        studentDateTo
      ) {
        setError(
          "From Date cannot be greater than To Date."
        );
        return [];
      }

      setStudentReportLoading(true);
      setError("");

      try {
        const payload = {
          fromDate: studentDateFrom,
          toDate: studentDateTo,
          organizationId,

          ...(globalBranchId
            ? {
                branch: String(globalBranchId),
              }
            : {}),

        };

        console.log("STUDENT ATTENDANCE REPORT REQUEST:", payload);

        const response = await API.post(
          "/api/reports/student-attendance-report/",
          payload
        );

        console.log("STUDENT ATTENDANCE REPORT RESPONSE:", response.data);

        const records = Array.isArray(
          response.data?.results
        )
          ? response.data.results
          : Array.isArray(response.data?.records)
          ? response.data.records
          : [];

        const mappedRecords =
          records.map((item, index) => ({
            id:
              item?.student_id ??
              `student-${index}`,
            student_name:
              item?.student_name ||
              item?.candidate_name ||
              item?.studentName ||
              item?.name ||
              "Unknown Student",
            student_code:
              item?.admission_code ||
              item?.student_code ||
              "—",
            working_days:
              item?.working_days ?? 0,
            present_days:
              item?.present_days ?? 0,
            absent_days:
              item?.absent_days ?? 0,
            half_days:
              item?.half_days ?? 0,
            leave_days:
              item?.leave_days ?? 0,
            attendance_percentage:
              item?.attendance_percentage ?? 0,
          }));

        setStudentRows(mappedRecords);
        return mappedRecords;
      } catch (fetchError) {
        console.error(
          "Student report error:",
          fetchError
        );

        setStudentRows([]);
        setError(
          getErrorMessage(fetchError)
        );

        return [];
      } finally {
        setStudentReportLoading(false);
      }
    },
    [
      organizationId,
      studentDateFrom,
      studentDateTo,
      globalBranchId,
      studentId,
    ]
  );

  /* =========================================================
     GENERATE REPORT + OPEN PDF COLOR PICKER
  ========================================================= */

  const handleGenerateReport = useCallback(
    async (type) => {
      const reportRows =
        type === "staff"
          ? await generateStaffReport()
          : await generateStudentReport();

      if (!reportRows?.length) {
        return;
      }

      setTempPdfThemeColor(
        normalizeHex(
          pdfThemeColor,
          DEFAULT_PDF_COLORS.primary
        )
      );

      setTempPdfTextColor(
        normalizeHex(
          pdfTextColor,
          DEFAULT_PDF_COLORS.bodyText
        )
      );

      setPdfReportType(type);
    },
    [
      generateStaffReport,
      generateStudentReport,
      pdfThemeColor,
      pdfTextColor,
    ]
  );

  /* =========================================================
     INITIAL REPORT LOAD / GLOBAL BRANCH CHANGE / TAB CHANGE
  ========================================================= */

  useEffect(() => {
    if (!organizationId || !globalBranchId) {
      return;
    }

    if (reportTab === "staff") {
      generateStaffReport();
    } else if (reportTab === "student") {
      generateStudentReport();
    }
  }, [
    organizationId,
    globalBranchId,
    reportTab,
  ]);

  /* =========================================================
     RESET
  ========================================================= */

  const resetStaff = () => {
    setStaffBranch(globalBranchId ? String(globalBranchId) : "");
    setStaffEmployeeId("");
    setStaffDateFrom(getOneMonthAgo());
    setStaffDateTo(getToday());
    setStaffRows([]);
    setStaffSearch("");
    setError("");
  };

  const resetStudent = () => {
    setStudentBranch(globalBranchId ? String(globalBranchId) : "");
    setStudentId("");
    setStudentDateFrom(getOneMonthAgo());
    setStudentDateTo(getToday());
    setStudentRows([]);
    setStudentSearch("");
    setError("");
  };

  /* =========================================================
     BRANCH LABEL
  ========================================================= */

  const getSelectedBranchName = useCallback(
    (branchId) => {
      if (!branchId) {
        return "All Branches";
      }

      const branch = (branchList || []).find(
        (item) =>
          String(getBranchId(item)) ===
          String(branchId)
      );

      return branch
        ? getBranchName(branch)
        : `Branch ${branchId}`;
    },
    [branchList]
  );

  /* =========================================================
     PDF CREATOR
  ========================================================= */

  const createPdf = ({
    type,
    rows,
    themeColor,
    textColor,
  }) => {
    const isStaff = type === "staff";

    const title = isStaff
      ? "Staff Attendance Report"
      : "Student Attendance Report";

    const branchId = String(globalBranchId || "");
    const fromDate = isStaff
      ? staffDateFrom
      : studentDateFrom;
    const toDate = isStaff
      ? staffDateTo
      : studentDateTo;

    const doc = new jsPDF({
      orientation: "landscape",
      unit: "mm",
      format: "a4",
    });

    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const margin = 8;
    const themeRgb = hexToRgb(themeColor);
    const textRgb = hexToRgb(textColor);

    const drawPdfHeader = () => {
      doc.setFillColor(...themeRgb);
      doc.rect(0, 0, pageWidth, 31, "F");

      if (branchLogo) {
        try {
          doc.addImage(
            branchLogo,
            "PNG",
            margin,
            5,
            21,
            21
          );
        } catch (logoError) {
          console.warn(
            "Unable to add branch logo:",
            logoError
          );
        }
      }

      doc.setTextColor(...hexToRgb(DEFAULT_PDF_COLORS.headerText));
      doc.setFont("helvetica", "bold");
      doc.setFontSize(16);

      doc.text(
        organizationName,
        pageWidth / 2,
        12,
        { align: "center" }
      );

      doc.setFontSize(10.5);
      doc.setFont("helvetica", "normal");

      doc.text(
        globalBranchName || `Branch ${branchId}`,
        pageWidth / 2,
        18,
        { align: "center" }
      );

      doc.setFont("helvetica", "bold");
      doc.setFontSize(9.5);

      doc.text(
        title,
        pageWidth / 2,
        24,
        { align: "center" }
      );

      doc.setFont("helvetica", "normal");
      doc.setFontSize(7.5);

      doc.text(
        `Generated: ${new Date().toLocaleString()}`,
        pageWidth - margin,
        13,
        { align: "right" }
      );
    };

    drawPdfHeader();

    let y = 37;

    doc.setTextColor(...textRgb);
    doc.setFontSize(8);

    doc.setFont("helvetica", "bold");
    doc.text("Filters", margin, y);
    doc.setFont("helvetica", "normal");
    y += 4;

    doc.text(
      `Branch: ${globalBranchName || `Branch ${branchId}`}    From: ${formatDate(fromDate)}    To: ${formatDate(toDate)}`,
      margin,
      y
    );

    y += 4;

    doc.text(
      isStaff
        ? `Employee: ${
            staffEmployeeId
              ? safeText(
                  employeeOptions.find(
                    (item) =>
                      String(
                        item?.id ??
                        item?.employee_id ??
                        item?.value
                      ) === String(staffEmployeeId)
                  )?.name ||
                    employeeOptions.find(
                      (item) =>
                        String(
                          item?.id ??
                          item?.employee_id ??
                          item?.value
                        ) === String(staffEmployeeId)
                    )?.full_name ||
                    staffEmployeeId
                )
              : "All Employees"
          }`
        : `Student: ${
            studentId
              ? safeText(
                  studentOptions.find(
                    (item) =>
                      String(
                        item?.id ??
                        item?.student_id ??
                        item?.value
                      ) === String(studentId)
                  )?.name ||
                    studentOptions.find(
                      (item) =>
                        String(
                          item?.id ??
                          item?.student_id ??
                          item?.value
                        ) === String(studentId)
                    )?.full_name ||
                    studentId
                )
              : "All Students"
          }`,
      margin,
      y
    );

    y += 5;

    doc.setFont("helvetica", "bold");
    doc.text("Summary", margin, y);
    doc.setFont("helvetica", "normal");
    y += 4;

    if (isStaff) {
      const present = rows.filter(
        (row) =>
          String(row?.status || "").toLowerCase() === "present"
      ).length;
      const absent = rows.filter(
        (row) =>
          String(row?.status || "").toLowerCase() === "absent"
      ).length;
      const halfDay = rows.filter(
        (row) =>
          String(row?.status || "").toLowerCase() === "half_day"
      ).length;
      const onLeave = rows.filter(
        (row) =>
          ["on_leave", "leave"].includes(
            String(row?.status || "").toLowerCase()
          )
      ).length;

      doc.text(
        `Total: ${rows.length}   |   Present: ${present}   |   Absent: ${absent}   |   Half Day: ${halfDay}   |   On Leave: ${onLeave}`,
        margin,
        y
      );
    } else {
      const totalPresent = rows.reduce(
        (sum, row) => sum + Number(row?.present_days || 0),
        0
      );
      const totalAbsent = rows.reduce(
        (sum, row) => sum + Number(row?.absent_days || 0),
        0
      );
      const totalHalf = rows.reduce(
        (sum, row) => sum + Number(row?.half_days || 0),
        0
      );
      const totalLeave = rows.reduce(
        (sum, row) => sum + Number(row?.leave_days || 0),
        0
      );

      doc.text(
        `Students: ${rows.length}   |   Present Days: ${totalPresent}   |   Absent Days: ${totalAbsent}   |   Half Days: ${totalHalf}   |   Leave Days: ${totalLeave}`,
        margin,
        y
      );
    }

    y += 6;

    const headColumns = isStaff
      ? [
          "S.No.",
          "Employee Name",
          "Employee Code",
          "Date",
          "Day",
          "Status",
          "Time In",
          "Time Out",
          "Total Hours",
          "Remark",
        ]
      : [
          "S.No.",
          "Student Name",
          "Admission Code",
          "Working Days",
          "Present Days",
          "Absent Days",
          "Half Days",
          "Leave Days",
          "Attendance %",
        ];

    const body = rows.map((item, index) =>
      isStaff
        ? [
            index + 1,
            safeText(item.employee_name),
            safeText(item.employee_code),
            formatDate(item.date),
            safeText(item.day),
            formatStatus(item.status),
            safeText(item.time_in),
            safeText(item.time_out),
            safeText(item.total_hours),
            safeText(item.remark),
          ]
        : [
            index + 1,
            safeText(item.student_name),
            safeText(item.student_code),
            safeText(item.working_days, "0"),
            safeText(item.present_days, "0"),
            safeText(item.absent_days, "0"),
            safeText(item.half_days, "0"),
            safeText(item.leave_days, "0"),
            `${safeText(item.attendance_percentage, "0")} %`,
          ]
    );

    const columnStyles = isStaff
      ? {
          0: { cellWidth: 11 },
          1: { cellWidth: 34 },
          2: { cellWidth: 24 },
          3: { cellWidth: 22 },
          4: { cellWidth: 16 },
          5: { cellWidth: 20 },
          6: { cellWidth: 22 },
          7: { cellWidth: 22 },
          8: { cellWidth: 24 },
          9: { cellWidth: 43 },
        }
      : {
          0: { cellWidth: 12 },
          1: { cellWidth: 48 },
          2: { cellWidth: 30 },
          3: { cellWidth: 25 },
          4: { cellWidth: 25 },
          5: { cellWidth: 25 },
          6: { cellWidth: 22 },
          7: { cellWidth: 24 },
          8: { cellWidth: 25 },
        };

    autoTable(doc, {
      startY: y,
      head: [headColumns],
      body: body.length
        ? body
        : [["", "No records found", ...Array(headColumns.length - 2).fill("")]],
      theme: "grid",
      tableWidth: pageWidth - margin * 2,
      margin: {
        left: margin,
        right: margin,
        bottom: 12,
      },
      styles: {
        font: "helvetica",
        fontSize: isStaff ? 6.4 : 7.2,
        textColor: textRgb,
        cellPadding: 1.7,
        overflow: "linebreak",
        valign: "middle",
        halign: "center",
        lineWidth: 0.1,
      },
      headStyles: {
        fillColor: themeRgb,
        textColor: hexToRgb(DEFAULT_PDF_COLORS.tableHeaderText),
        fontStyle: "bold",
        fontSize: isStaff ? 6.6 : 7.4,
        halign: "center",
        valign: "middle",
      },
      alternateRowStyles: {
        fillColor: hexToRgb(DEFAULT_PDF_COLORS.alternateRow),
      },
      columnStyles,
      willDrawPage: () => {
        drawPdfHeader();
      },
      didDrawPage: () => {
        const currentPage =
          doc.internal.getNumberOfPages();

        doc.setFont("helvetica", "normal");
        doc.setFontSize(7);
        doc.setTextColor(...hexToRgb(DEFAULT_PDF_COLORS.mutedText));

        doc.text(
          title,
          margin,
          pageHeight - 5
        );

        doc.text(
          `Page ${currentPage}`,
          pageWidth - margin,
          pageHeight - 5,
          { align: "right" }
        );
      },
    });

    return doc;
  };

  /* =========================================================
     PDF GENERATION
     Frontend jsPDF using already loaded report rows.
  ========================================================= */

  const generateCustomPdf = async () => {
    if (!pdfReportType) {
      return;
    }

    const type = pdfReportType;
    const data =
      type === "staff"
        ? staffRows
        : studentRows;

    if (!data.length) {
      notify(
        "Please generate the report first.",
        "warning"
      );
      return;
    }

    try {
      setPdfLoading(true);
      setError("");

      const doc = createPdf({
        type,
        rows: data,
        themeColor: normalizeHex(
          tempPdfThemeColor,
          DEFAULT_PDF_COLORS.primary
        ),
        textColor: normalizeHex(
          tempPdfTextColor,
          DEFAULT_PDF_COLORS.bodyText
        ),
      });

      updatePdfThemeColor(
        normalizeHex(
          tempPdfThemeColor,
          DEFAULT_PDF_COLORS.primary
        )
      );
      updatePdfTextColor(
        normalizeHex(
          tempPdfTextColor,
          DEFAULT_PDF_COLORS.bodyText
        )
      );

      doc.save(
        `${
          type === "staff"
            ? "Staff"
            : "Student"
        }_Attendance_Report_${getToday()}.pdf`
      );

      setPdfReportType(null);
      notify(
        "PDF generated successfully.",
        "success"
      );
    } catch (pdfError) {
      console.error(
        "Frontend PDF generation error:",
        pdfError
      );
      setError(getErrorMessage(pdfError));
      notify(
        getErrorMessage(pdfError),
        "error"
      );
    } finally {
      setPdfLoading(false);
    }
  };

  /* =========================================================
     EXCEL EXPORT
     BACKEND EXPORT ENDPOINT
  ========================================================= */

  const downloadExcel = async (type) => {
    const isStaff = type === "staff";

    setDownloadLoading(true);
    setError("");

    try {
      const payload = isStaff
        ? {
            fromDate: staffDateFrom,
            toDate: staffDateTo,
            organizationId,
            ...(globalBranchId
              ? { branch: String(globalBranchId) }
              : {}),
            ...(staffEmployeeId
              ? { employeeId: staffEmployeeId }
              : {}),
          }
        : {
            fromDate: studentDateFrom,
            toDate: studentDateTo,
            organizationId,
            ...(globalBranchId
              ? { branch: String(globalBranchId) }
              : {}),
          };

      const endpoint = isStaff
        ? "/api/reports/attendance-report/export/excel/"
        : "/api/reports/student-attendance-report/export/excel/";

      const response = await API.post(
        endpoint,
        payload,
        { responseType: "blob" }
      );

      const blob = new Blob([response.data], {
        type:
          "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });

      const url = URL.createObjectURL(blob);
      const anchor = document.createElement("a");

      anchor.href = url;
      anchor.download = `${
        isStaff ? "Staff" : "Student"
      }_Attendance_Report_${getToday()}.xlsx`;

      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();

      setTimeout(
        () => URL.revokeObjectURL(url),
        10000
      );
    } catch (excelError) {
      console.error(
        "Excel export error:",
        excelError
      );

      setError(getErrorMessage(excelError));
    } finally {
      setDownloadLoading(false);
    }
  };

  /* =========================================================
     COLUMNS
  ========================================================= */

  const staffColumns = [
    "employee_name",
    "employee_code",
    "date",
    "day",
    "status",
    "time_in",
    "time_out",
    "total_hours",
    "remark",
  ];

  const studentColumns = [
    "student_name",
    "student_code",
    "working_days",
    "present_days",
    "absent_days",
    "half_days",
    "leave_days",
    "attendance_percentage",
  ];

  const staffSubtitle = `${organizationName} • ${getSelectedBranchName(
    staffBranch
  )}`;

  const studentSubtitle = `${organizationName} • ${getSelectedBranchName(
    studentBranch
  )}`;

  /* =========================================================
     UI
  ========================================================= */

  return (
    <Box
      sx={{
        p: {
          xs: 1.5,
          md: 3,
        },
        minHeight: "100%",
        backgroundColor: "unset",
      }}
    >
     {/* =====================================================
    TABS
====================================================== */}

<Box
  sx={{
    mb: 2,
    p: 0.5,
    borderRadius: 2,
    borderBottom: "1px solid",
    borderColor: "divider",
    overflowX: "auto",
  }}
>
  <Tabs
    value={reportTab}
    onChange={(_, value) => setReportTab(value)}
    variant="scrollable"
    scrollButtons="auto"
    allowScrollButtonsMobile
    sx={{
      minHeight: 42,

      "& .MuiTabs-flexContainer": {
        gap: 0.5,
      },

      "& .MuiTabs-indicator": {
        display: "none",
      },

      "& .MuiTab-root": {
        minHeight: 42,
        minWidth: "auto",
        px: {
          xs: 1.5,
          sm: 2.2,
        },
        borderRadius: 1.5,
        color: "text.primary",
        fontWeight: 600,
        textTransform: "none",
        fontSize: {
          xs: 13,
          sm: 14,
        },
        opacity: 1,
        transition: "all 0.2s ease",

        "&:hover": {
          backgroundColor: "action.hover",
          color: "text.primary",
        },
      },

      "& .MuiTab-root.Mui-selected": {
        backgroundColor: "primary.main",
        color: "primary.contrastText",

        "&:hover": {
          backgroundColor: "primary.dark",
        },
      },

      "& .MuiTabs-scrollButtons": {
        color: "text.primary",

        "&.Mui-disabled": {
          opacity: 0.3,
        },
      },
    }}
  >
    <Tab
      value="staff"
      label="Staff Attendance Report"
    />

    <Tab
      value="student"
      label="Student Attendance Report"
    />
  </Tabs>
</Box>
      {/* =====================================================
          ERROR
      ====================================================== */}

      {error && (
        <Alert
          severity="error"
          sx={{
            mb: 2,
            borderRadius: 2,
          }}
          onClose={() => setError("")}
        >
          {error}
        </Alert>
      )}

      {/* =====================================================
          STAFF
      ====================================================== */}

      {reportTab === "staff" && (
        <Card
          elevation={0}
          sx={{
            borderRadius: 2.5,
        
            backgroundColor: "unset",
          }}
        >
          <ReportHeader
            title="Staff Attendance Report"
            subtitle={staffSubtitle}
            search={staffSearch}
            setSearch={setStaffSearch}
            onFilter={() =>
              setStaffFilterOpen(true)
            }
            onGenerate={() => handleGenerateReport("staff")}
            onExcel={() =>
              downloadExcel("staff")
            }
            loading={
              staffReportLoading || downloadLoading
            }
            disableExport={
              !staffRows.length
            }
          />

     

          <CardContent
            sx={{
              p: {
                xs: 1.5,
                md: 2,
              },
              "&:last-child": {
                pb: {
                  xs: 1.5,
                  md: 2,
                },
              },
            }}
          >
            <ReportTable
              title="Staff attendance records"
              rows={staffRows}
              columns={staffColumns}
              loading={
                staffReportLoading
              }
              search={
                staffSearch
              }
              setSearch={
                setStaffSearch
              }
            />
          </CardContent>
        </Card>
      )}

      {/* =====================================================
          STUDENT
      ====================================================== */}

      {reportTab === "student" && (
        <Card
          elevation={0}
          sx={{
            borderRadius: 2.5,
          
            backgroundColor: "unset",
          }}
        >
          <ReportHeader
            title="Student Attendance Report"
            subtitle={studentSubtitle}
            search={studentSearch}
            setSearch={setStudentSearch}
            onFilter={() =>
              setStudentFilterOpen(true)
            }
            onGenerate={() => handleGenerateReport("student")}
            onExcel={() =>
              downloadExcel("student")
            }
            loading={
              studentReportLoading || downloadLoading
            }
            disableExport={
              !studentRows.length
            }
          />

         

          <CardContent
            sx={{
              p: {
                xs: 1.5,
                md: 2,
              },
              "&:last-child": {
                pb: {
                  xs: 1.5,
                  md: 2,
                },
              },
            }}
          >
            <ReportTable
              title="Student attendance records"
              rows={studentRows}
              columns={
                studentColumns
              }
              loading={
                studentReportLoading
              }
              search={
                studentSearch
              }
              setSearch={
                setStudentSearch
              }
            />
          </CardContent>
        </Card>
      )}

      {/* =====================================================
          STAFF FILTER
      ====================================================== */}

      <FilterModal
        open={staffFilterOpen}
        onClose={() =>
          setStaffFilterOpen(false)
        }
        title="Staff Attendance"
        selectedId={
          staffEmployeeId
        }
        setSelectedId={
          setStaffEmployeeId
        }
        options={employeeOptions}
        loadingOptions={
          employeeLoading
        }
        getOptionId={
          getEmployeeId
        }
        getOptionLabel={
          getEmployeeLabel
        }
        optionLabel="Employee"
        dateFrom={
          staffDateFrom
        }
        dateTo={staffDateTo}
        setDateFrom={
          setStaffDateFrom
        }
        setDateTo={
          setStaffDateTo
        }
        onReset={resetStaff}
        onApply={() => {
          setStaffFilterOpen(false);
          generateStaffReport();
        }}
      />

      {/* =====================================================
          STUDENT FILTER
      ====================================================== */}

      <FilterModal
        open={studentFilterOpen}
        onClose={() =>
          setStudentFilterOpen(false)
        }
        title="Student Attendance"
        selectedId={studentId}
        setSelectedId={setStudentId}
        options={studentOptions}
        loadingOptions={
          studentLoading
        }
        getOptionId={
          getStudentId
        }
        getOptionLabel={
          getStudentLabel
        }
        optionLabel="Student"
        dateFrom={
          studentDateFrom
        }
        dateTo={studentDateTo}
        setDateFrom={
          setStudentDateFrom
        }
        setDateTo={
          setStudentDateTo
        }
        onReset={resetStudent}
        onApply={() => {
          setStudentFilterOpen(
            false
          );
          generateStudentReport();
        }}
      />

      {/* =====================================================
          PDF COLOR
      ====================================================== */}

      <PdfColorModal reportTitle={pdfReportType === "student" ? "Student Attendance Report" : "Staff Attendance Report"}
        open={Boolean(pdfReportType)}
        onClose={() =>
          setPdfReportType(null)
        }
        themeColor={
          tempPdfThemeColor
        }
        textColor={
          tempPdfTextColor
        }
        setThemeColor={
          setTempPdfThemeColor
        }
        setTextColor={
          setTempPdfTextColor
        }
        onReset={() => {
          setTempPdfThemeColor(
            normalizeHex(
              pdfThemeColor,
              DEFAULT_PDF_COLORS.primary
            )
          );

          setTempPdfTextColor(
            normalizeHex(
              pdfTextColor,
              DEFAULT_PDF_COLORS.bodyText
            )
          );
        }}
        onGenerate={
          generateCustomPdf
        }
        loading={pdfLoading}
      />

    </Box>
  );
};

export default AttendanceReport;
